import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { EvaluationService } from '@services/core/evaluation.service';
import { ModuleService } from '@services/core/module.service';
import { moduleGroups } from '@shared/module.utils';
import { of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';

function routeName(module: any): string {
  return pickRoute(module);
}

function pickRoute(module: any): string {
  return module?.routeName || module?.acronym || '';
}

function findByRoute(modules: any[], route: string) {
  return modules.find((item) => isRoute(item, route)) || null;
}

function isRoute(module: any, route: string): boolean {
  return routeName(module).toLowerCase() === route.toLowerCase();
}

export const evaluationTemplateResolver: ResolveFn<any> = (route) => {
  const evaluationService = inject(EvaluationService);
  const moduleService = inject(ModuleService);
  const yearSelected = new Date().getFullYear();
  const routeSegment = route.paramMap.get('moduleRoute') || '';
  return fetchModules(moduleService, evaluationService, routeSegment, yearSelected);
};

function fetchModules(moduleService: ModuleService, evaluationService: EvaluationService, segment: string, year: number) {
  return moduleService.getActiveModules().pipe(
    switchMap((modules: any[]) => pickModule(evaluationService, modules, segment, year))
  );
}

function pickModule(evaluationService: EvaluationService, modules: any[], segment: string, year: number) {
  if (!modules?.length) return emptyModules();
  return withModule(evaluationService, modules, segment, year)
}

function emptyModules() {
  return of({ modules: [], groups: [], categories: [], questions: [] });
}

function withModule(evaluationService: EvaluationService, modules: any[], segment: string, year: number) {
  const module = findByRoute(modules, segment) || modules[0];
  return withGroups(evaluationService, modules, module, year)
}

function withGroups(evaluationService: EvaluationService, modules: any[], module: any, year: number) {
  const groups = moduleGroups(module);
  if (!groups.length) return emptyGroups(modules, module, groups);
  return fetchTemplate(evaluationService, modules, module, groups, year);
}

function emptyGroups(modules: any[], module: any, groups: any[]) {
  return of({ modules, module, groups, categories: [], questions: [] });
}

function fetchTemplate(evaluationService: EvaluationService, modules: any[], module: any, groups: any[], year: number) {
  return evaluationService.getCategories(module.id, year, groups[0]).pipe(
    switchMap((categories: any[]) => pickCategory(evaluationService, modules, module, groups, categories))
  );
}

function pickCategory(evaluationService: EvaluationService, modules: any[], module: any, groups: any[], categories: any[]) {
  if (!categories?.length) return emptyCategories(modules, module, groups);
  return loadQuestions(evaluationService, modules, module, groups, categories)
}

function emptyCategories(modules: any[], module: any, groups: any[]) {
  return of({ modules, module, groups, categories: [], questions: [] });
}

function loadQuestions(evaluationService: EvaluationService, modules: any[], module: any, groups: any[], categories: any[]) {
  return evaluationService.getQuestions(firstCategory(categories)).pipe(
    map((questions: any) => buildTemplate(modules, module, groups, categories, questions))
  );
}

function firstCategory(categories: any[]): string {
  return categories[0].id
}

function buildTemplate(modules: any[], module: any, groups: any[], categories: any[], questions: any[]) {
  return { modules, module, groups, categories, questions };
}
